import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Folder, TrendingUp, ShieldCheck, Award, CalendarDays, LogOut, Lock, ShoppingBag } from "lucide-react";
import { api, errMsg, LOGO, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  ["perfil", "Perfil", ShieldCheck],
  ["informes", "Informes", FileText],
  ["materiales", "Material", Folder],
  ["seguimiento", "Evolución", TrendingUp],
  ["pasaporte", "Pasaporte", Award],
  ["consentimientos", "Consentimientos", Lock],
  ["compras", "Mis compras", ShoppingBag],
  ["turnos", "Turnos", CalendarDays],
];

export default function Portal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("perfil");
  const [d, setD] = useState(null);
  const [consents, setConsents] = useState([]);
  const [firma, setFirma] = useState("");
  const [orders, setOrders] = useState([]);

  const load = () => {
    api.get("/me/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/me/consents").then((r) => setConsents(r.data)).catch(() => {});
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const firmar = async (id) => {
    if (!firma.trim()) return toast.error("Escribí tu nombre completo como firma digital");
    try { await api.post(`/me/consents/${id}/sign`, { firma }); toast.success("Consentimiento firmado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openPdf = (path) => {
    api.get(path, { responseType: "blob" })
      .then((r) => window.open(URL.createObjectURL(r.data), "_blank"))
      .catch(() => toast.error("No fue posible abrir el documento"));
  };

  if (!d) return <div className="p-12 text-sm text-[#5e5a56]">Cargando portal...</div>;
  const pendientes = consents.filter((c) => !c.firmado);

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[#f9f8f6]">
      <header className="border-b border-[#dcd5ce] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Nilo" className="h-10 w-10 rounded-full" />
            <span><span className="block serif text-lg leading-none">Portal Paciente</span><span className="label-xs">Nilo Espacio Psicopedagógico</span></span>
          </Link>
          <button onClick={logout} data-testid="portal-logout" className="flex items-center gap-2 text-xs text-[#5e5a56] hover:text-[#c5a059]">
            <LogOut size={14} /> Salir
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="text-4xl tracking-tight">Hola, {user?.nombre}</h1>
        {!d.consentimientos_completos && (
          <div data-testid="consent-block-alert" className="mt-6 border border-[#c5a059] bg-[#f4f1ec] p-5 text-sm">
            Tenés {pendientes.length} consentimiento(s) pendiente(s). Los informes permanecen bloqueados hasta que estén firmados.
          </div>
        )}

        <nav className="mt-8 flex flex-wrap gap-2 border-b border-[#dcd5ce] pb-3">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`portal-tab-${k}`}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${tab === k ? "btn-gold" : "text-[#5e5a56] hover:text-[#c5a059]"}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </nav>

        <div className="mt-8">
          {tab === "perfil" && (
            <div className="card-nilo grid gap-5 p-8 sm:grid-cols-2" data-testid="portal-perfil">
              {[["Nombre", `${d.perfil.nombre} ${d.perfil.apellido}`], ["DNI", d.perfil.dni], ["Email", d.perfil.email || "-"],
                ["Teléfono", d.perfil.telefono || "-"], ["Escuela", d.perfil.escuela || "-"], ["Grado", d.perfil.grado || "-"]].map(([k, v]) => (
                <div key={k}><p className="label-xs">{k}</p><p className="mt-1 text-sm">{v}</p></div>
              ))}
            </div>
          )}

          {tab === "informes" && (
            <div data-testid="portal-informes" className="space-y-3">
              {!d.consentimientos_completos ? (
                <p className="card-nilo p-8 text-sm text-[#5e5a56]">Visualización bloqueada: completá la firma de todos los consentimientos.</p>
              ) : d.informes.length === 0 ? (
                <p className="card-nilo p-8 text-sm text-[#5e5a56]">Todavía no hay informes compartidos.</p>
              ) : d.informes.map((r) => (
                <div key={r.id} className="card-nilo flex items-center justify-between p-6">
                  <div><p className="serif text-xl">{r.titulo}</p><p className="label-xs mt-1">{r.tipo} · {r.fecha}</p></div>
                  <button data-testid={`informe-pdf-${r.id}`} onClick={() => openPdf(`/reports/${r.id}/pdf`)} className="btn-outline-ink rounded-full px-5 py-2 text-xs uppercase tracking-[0.14em]">Ver PDF</button>
                </div>
              ))}
            </div>
          )}

          {tab === "materiales" && (
            <div data-testid="portal-materiales" className="grid gap-4 md:grid-cols-2">
              {d.materiales.length === 0 && <p className="card-nilo p-8 text-sm text-[#5e5a56]">Sin material compartido por el momento.</p>}
              {d.materiales.map((m) => (
                <div key={m.id} className="card-nilo p-6">
                  <span className="label-xs">{m.categoria}</span>
                  <p className="mt-3 serif text-xl">{m.titulo}</p>
                  <p className="mt-2 text-sm text-[#5e5a56]">{m.descripcion}</p>
                  {m.contenido && <pre className="mt-3 whitespace-pre-wrap text-xs text-[#4a4744]">{m.contenido}</pre>}
                  {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="mt-4 inline-block text-sm text-[#c5a059]">Abrir recurso</a>}
                </div>
              ))}
            </div>
          )}

          {tab === "seguimiento" && (
            <div data-testid="portal-seguimiento" className="space-y-4">
              {d.seguimiento.length === 0 && <p className="card-nilo p-8 text-sm text-[#5e5a56]">Sin registros habilitados aún.</p>}
              {d.seguimiento.map((t) => (
                <div key={t.id} className="card-nilo border-l-2 border-l-[#c5a059] p-6">
                  <p className="label-xs">{t.fecha} · {t.tipo}</p>
                  {t.objetivos && <p className="mt-3 text-sm"><b>Objetivos:</b> {t.objetivos}</p>}
                  {t.avances && <p className="mt-1 text-sm"><b>Avances:</b> {t.avances}</p>}
                  {t.proximos_objetivos && <p className="mt-1 text-sm"><b>Próximos objetivos:</b> {t.proximos_objetivos}</p>}
                </div>
              ))}
            </div>
          )}

          {tab === "pasaporte" && (
            <div data-testid="portal-pasaporte" className="card-nilo p-10">
              <p className="label-xs">Pasaporte de aprendizaje</p>
              <h2 className="mt-3 text-3xl">Tus logros</h2>
              {d.logros.length === 0 ? <p className="mt-4 text-sm text-[#5e5a56]">Pronto vas a ver acá tus logros habilitados.</p> : (
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {d.logros.map((l, i) => (
                    <div key={i} className="flex items-start gap-3 border border-[#dcd5ce] bg-[#f4f1ec] p-5">
                      <Award size={18} className="mt-0.5 text-[#c5a059]" /> <span className="text-sm">{l}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "consentimientos" && (
            <div data-testid="portal-consentimientos" className="space-y-4">
              <input data-testid="firma-input" placeholder="Firma digital: escribí tu nombre completo" value={firma} onChange={(e) => setFirma(e.target.value)}
                className="w-full max-w-lg border border-[#dcd5ce] bg-white px-4 py-3 text-sm outline-none focus:border-[#c5a059]" />
              {consents.map((c) => (
                <div key={c.id} className="card-nilo p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="serif text-xl">{c.tipo}</p>
                    {c.firmado ? (
                      <span className="text-xs uppercase tracking-[0.14em] text-[#4a6b53]">Firmado {c.fecha} {c.hora}</span>
                    ) : (
                      <button data-testid={`firmar-${c.id}`} onClick={() => firmar(c.id)} className="btn-gold rounded-full px-5 py-2 text-xs uppercase tracking-[0.14em]">Firmar</button>
                    )}
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[#5e5a56]">{c.texto}</p>
                  {c.firmado && (
                    <button data-testid={`consent-pdf-${c.id}`} onClick={() => openPdf(`/consents/${c.id}/pdf`)} className="mt-4 btn-outline-ink rounded-full px-5 py-2 text-xs uppercase tracking-[0.14em]">Descargar PDF</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "compras" && (
            <div data-testid="portal-compras" className="space-y-3">
              {orders.length === 0 && (
                <p className="card-nilo p-8 text-sm text-[#5e5a56]">
                  Todavía no tenés compras. Podés ver el material disponible en la tienda.
                </p>
              )}
              {orders.map((o) => (
                <div key={o.id} data-testid={`compra-${o.id}`} className="card-nilo flex flex-wrap items-center justify-between gap-4 p-6">
                  <div>
                    <p className="serif text-xl">{o.product_nombre}</p>
                    <p className="label-xs mt-1">{o.created_at?.slice(0, 10)} · ${Number(o.precio).toLocaleString("es-AR")} · {o.estado}</p>
                  </div>
                  {o.archivo_url ? (
                    <a href={o.archivo_url} target="_blank" rel="noreferrer" data-testid={`compra-descargar-${o.id}`}
                      className="btn-gold rounded-full px-5 py-2 text-xs uppercase tracking-[0.14em]">Descargar</a>
                  ) : (
                    <span className="text-xs text-[#8b8480]">Disponible al confirmarse el pago</span>
                  )}
                </div>
              ))}
              <Link to="/tienda" className="inline-block btn-outline-ink rounded-full px-6 py-3 text-xs uppercase tracking-[0.14em]">Ir a la tienda</Link>
            </div>
          )}

          {tab === "turnos" && (
            <div data-testid="portal-turnos" className="space-y-3">
              {d.turnos.length === 0 && <p className="card-nilo p-8 text-sm text-[#5e5a56]">No tenés turnos registrados.</p>}
              {d.turnos.map((t) => (
                <div key={t.id} className="card-nilo flex items-center justify-between p-6">
                  <p className="text-sm">{t.fecha} — {t.hora} hs</p>
                  <span className="label-xs">{t.estado}</span>
                </div>
              ))}
              <Link to="/contacto" className="inline-block btn-gold rounded-full px-6 py-3 text-xs uppercase tracking-[0.14em]">Solicitar turno</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
